import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'stepTab', 'stepBadge', 'stepLabel', 'stepPanel', 'stepCounter',
    'backBtn', 'nextBtn', 'submitWrapper',
    'preset', 'matchField', 'conditionHint',
    'field', 'travelEventsHelp', 'name',
    'rpmBandConfigFilter', 'rpmBandSelect', 'rpmBandsWrapper'
  ]

  static values = { currentStep: Number }

  connect() {
    this.applyPreset();
    this.showStep(this.currentStepValue || 0);
  }

  // ── Passos ────────────────────────────────────────────────────────────

  goToStep(event) {
    this.showStep(Number(event.currentTarget.dataset.stepIndex));
  }

  nextStep() {
    this.showStep(this.currentStepValue + 1);
  }

  previousStep() {
    this.showStep(this.currentStepValue - 1);
  }

  showStep(index) {
    const last = this.stepPanelTargets.length - 1;
    const step = Math.min(Math.max(index, 0), last);
    this.currentStepValue = step;

    this.stepPanelTargets.forEach((panel, i) => panel.classList.toggle('hidden', i !== step));
    this.stepBadgeTargets.forEach((badge, i) => this.paintBadge(badge, i, step));
    this.stepLabelTargets.forEach((label, i) => {
      label.classList.toggle('text-blue-600', i === step);
      label.classList.toggle('dark:text-blue-400', i === step);
      label.classList.toggle('text-gray-500', i !== step);
      label.classList.toggle('dark:text-gray-400', i !== step);
    });

    this.backBtnTarget.classList.toggle('invisible', step === 0);
    this.nextBtnTarget.classList.toggle('hidden', step === last);
    this.submitWrapperTarget.classList.toggle('hidden', step !== last);
    this.stepCounterTarget.textContent = `${step + 1} / ${last + 1}`;

    this.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  paintBadge(badge, index, step) {
    const active = index === step;
    const done = index < step;

    badge.classList.toggle('bg-blue-600', active);
    badge.classList.toggle('text-white', active || done);
    badge.classList.toggle('border-blue-600', active || done);
    badge.classList.toggle('bg-blue-400', done && !active);
    badge.classList.toggle('bg-transparent', !active && !done);
    badge.classList.toggle('text-gray-400', !active && !done);
    badge.classList.toggle('border-gray-300', !active && !done);
    badge.classList.toggle('dark:border-gray-600', !active && !done);
  }

  // ── Preset (passo 1) ──────────────────────────────────────────────────

  selectPreset() {
    this.applyPreset();
    this.nextStep();
  }

  // O preset escolhido alimenta os dois campos que o modelo persiste e
  // decide quais campos do passo 2 ficam visíveis. Sem preset (regra nova,
  // nada marcado) o passo 2 fica vazio e só instrui a voltar.
  applyPreset() {
    const preset = this.presetTargets.find((input) => input.checked);
    const match = preset ? JSON.parse(preset.dataset.match) : {};

    for (const field of this.matchFieldTargets) {
      field.value = match[field.dataset.attribute] || '';
    }

    const visible = preset ? preset.dataset.fields.split(' ') : [];

    for (const field of this.fieldTargets) {
      field.classList.toggle('hidden', !visible.includes(field.dataset.fieldName));
    }

    this.conditionHintTarget.textContent = preset
      ? this.presetLabel(preset)
      : this.conditionHintTarget.dataset.noPreset;

    // Em "evento esperado não registrado" a mesma lista significa o evento
    // que deveria ter chegado, não o que dispara o alerta.
    if (this.hasTravelEventsHelpTarget) {
      const help = this.travelEventsHelpTarget.dataset;
      this.travelEventsHelpTarget.textContent = preset && preset.dataset.fields.includes('after_travel_event')
        ? help.expected
        : help.default;
    }

    this.suggestName(preset);
  }

  presetLabel(preset) {
    return preset.closest('label').querySelector('span:last-of-type').textContent.trim();
  }

  // Só preenche um nome vazio — nunca sobrescreve o que a pessoa escreveu
  // nem o nome de uma regra já salva.
  suggestName(preset) {
    if (!this.hasNameTarget || !preset || this.nameTarget.value.trim() !== '') return;

    this.nameTarget.value = preset.closest('label').querySelector('span').textContent.trim();
  }

  // ── Faixas de RPM ─────────────────────────────────────────────────────

  // Bandas só ficam visíveis/selecionáveis depois que uma config é
  // escolhida — evita a lista antiga misturando bandas de todas as configs
  // num único select. Esconder e desmarcar via JS (em vez de recarregar a
  // página) porque as opções já vêm todas no HTML; só o filtro muda.
  filterRpmBands() {
    const configId = this.rpmBandConfigFilterTarget.value;

    for (const option of this.rpmBandSelectTarget.options) {
      const matches = configId !== '' && option.dataset.configId === configId;
      option.hidden = !matches;
      if (!matches) option.selected = false;
    }

    this.rpmBandsWrapperTarget.dispatchEvent(new Event('react-select:refresh'));
  }
}
